/** Shared display helpers for the CRM pages (dates, names, badges, money). */

import type { AccentTone } from '@/ui';
import type { Contact, CrmEntityType, GeoFields } from '@/lib/api/crm';

/** The backend serializes naive UTC datetimes (no `Z`); normalize before parsing. */
export function parseUtc(value: string | null | undefined): Date | null {
  if (!value) return null;
  const iso = /(Z|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function timeAgo(value: string | null | undefined): string {
  const date = parseUtc(value);
  if (!date) return '—';
  const s = Math.floor((Date.now() - date.getTime()) / 1000);
  if (s < 5) return "à l'instant";
  if (s < 60) return `il y a ${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  return `il y a ${Math.floor(h / 24)} j`;
}

export function formatDate(value: string | null | undefined): string {
  const date = parseUtc(value);
  if (!date) return '—';
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function formatDateTime(value: string | null | undefined): string {
  const date = parseUtc(value);
  if (!date) return '—';
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Turns an ISO string into the `YYYY-MM-DDTHH:mm` value a datetime-local wants. */
export function toDatetimeLocal(value: string | null | undefined): string {
  const date = parseUtc(value);
  if (!date) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

export function contactName(contact: Pick<Contact, 'first_name' | 'last_name' | 'emails'>): string {
  const full = [contact.first_name, contact.last_name].filter(Boolean).join(' ').trim();
  if (full) return full;
  const primary =
    contact.emails.find((e) => e.primary)?.email ?? contact.emails[0]?.email ?? '';
  return primary || 'Contact sans nom';
}

export function contactInitials(
  contact: Pick<Contact, 'first_name' | 'last_name' | 'emails'>,
): string {
  const first = contact.first_name?.trim()?.[0] ?? '';
  const last = contact.last_name?.trim()?.[0] ?? '';
  const initials = `${first}${last}`.toUpperCase();
  if (initials) return initials;
  const email = contact.emails.find((e) => e.primary)?.email ?? contact.emails[0]?.email ?? '';
  return (email[0] ?? '?').toUpperCase();
}

export function primaryEmail(contact: Pick<Contact, 'emails'>): string {
  return contact.emails.find((e) => e.primary)?.email ?? contact.emails[0]?.email ?? '';
}

export function primaryPhone(contact: Pick<Contact, 'phones'>): string {
  return contact.phones.find((p) => p.primary)?.phone ?? contact.phones[0]?.phone ?? '';
}

export function formatMoney(value: number, currency = 'CAD'): string {
  try {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value} ${currency}`;
  }
}

// ---------------------------------------------------------------------------
// Badges / labels
// ---------------------------------------------------------------------------

const LIFECYCLE_BADGE: Record<string, string> = {
  lead: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-200',
  prospect: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-200',
  customer: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-200',
  influencer: 'bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-200',
  partner: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-200',
  other: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300',
};

export function lifecycleBadgeClass(stage: string): string {
  return LIFECYCLE_BADGE[stage] ?? LIFECYCLE_BADGE.other;
}

const LIFECYCLE_LABEL: Record<string, string> = {
  lead: 'Lead',
  prospect: 'Prospect',
  customer: 'Client',
  influencer: 'Influenceur',
  partner: 'Partenaire',
  other: 'Autre',
};

export function lifecycleLabel(stage: string): string {
  return LIFECYCLE_LABEL[stage] ?? stage;
}

/** Accent tone (for a `GridCard` accent strip / ribbon) keyed by lifecycle stage. */
const LIFECYCLE_ACCENT: Record<string, AccentTone> = {
  lead: 'primary',
  prospect: 'accent',
  customer: 'success',
  influencer: 'accent',
  partner: 'warning',
  other: 'primary',
};

export function lifecycleAccent(stage: string): AccentTone {
  return LIFECYCLE_ACCENT[stage] ?? 'primary';
}

// ---------------------------------------------------------------------------
// Entity taxonomy (contract §1) — badge / label / icon, one per
// `person | group | media | organisation | firm | agency | government |
// programme | other`. Same "pill" visual language as the lifecycle badge above
// (bg-*-100/text-*-700, dark:bg-*-900/50/dark:text-*-200) so it reads as the
// same design system, just a different colour per type.
// ---------------------------------------------------------------------------

const ENTITY_TYPE_BADGE: Record<CrmEntityType, string> = {
  person: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-200',
  group: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/50 dark:text-cyan-200',
  media: 'bg-pink-100 text-pink-700 dark:bg-pink-900/50 dark:text-pink-200',
  organisation: 'bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-200',
  firm: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-200',
  agency: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-200',
  government: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-200',
  programme: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-200',
  other: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300',
};

export function entityTypeBadgeClass(type: string): string {
  return ENTITY_TYPE_BADGE[type as CrmEntityType] ?? ENTITY_TYPE_BADGE.other;
}

const ENTITY_TYPE_LABEL: Record<CrmEntityType, string> = {
  person: 'Personne',
  group: 'Groupe',
  media: 'Média',
  organisation: 'Organisation',
  firm: 'Entreprise',
  agency: 'Agence',
  government: 'Gouvernement',
  programme: 'Programme',
  other: 'Autre',
};

export function entityTypeLabel(type: string): string {
  return ENTITY_TYPE_LABEL[type as CrmEntityType] ?? type;
}

const ENTITY_TYPE_ICON: Record<CrmEntityType, string> = {
  person: '👤',
  group: '👥',
  media: '📰',
  organisation: '🌐',
  firm: '🏢',
  agency: '🏬',
  government: '🏛️',
  programme: '📋',
  other: '▫️',
};

/** A small glyph prefix for the entity-type badge (no icon-font dependency). */
export function entityTypeIcon(type: string): string {
  return ENTITY_TYPE_ICON[type as CrmEntityType] ?? ENTITY_TYPE_ICON.other;
}

// ---------------------------------------------------------------------------
// Geo (contract §2) — address formatting. Every piece is optional; only the
// present ones are joined so a partial address never renders "null"/"undefined".
// ---------------------------------------------------------------------------

/** `"12 rue X, H2X 1Y4 Montréal, QC, CA"` — present fields only, or `null`. */
export function formatAddress(
  geo: Pick<GeoFields, 'address_line' | 'postal_code' | 'city' | 'region' | 'country'>,
): string | null {
  const line2 = [geo.postal_code, geo.city].filter((p) => !!p && p.trim() !== '').join(' ');
  const parts = [geo.address_line, line2, geo.region, geo.country].filter(
    (p) => !!p && p.trim() !== '',
  );
  return parts.length ? parts.join(', ') : null;
}

/** True when at least one geo field carries a value (drives conditional UI). */
export function hasAddress(geo: Partial<GeoFields>): boolean {
  return [geo.address_line, geo.postal_code, geo.city, geo.region, geo.country].some(
    (p) => !!p && p.trim() !== '',
  );
}

const DEAL_STATUS_LABEL: Record<string, string> = {
  open: 'Ouvert',
  won: 'Gagné',
  lost: 'Perdu',
};

export function dealStatusLabel(status: string): string {
  return DEAL_STATUS_LABEL[status] ?? status;
}

const REMINDER_STATUS_LABEL: Record<string, string> = {
  open: 'À faire',
  done: 'Fait',
  snoozed: 'Reporté',
};

export function reminderStatusLabel(status: string): string {
  return REMINDER_STATUS_LABEL[status] ?? status;
}

const ACTIVITY_LABEL: Record<string, string> = {
  merged: 'Fusion',
  assigned: 'Assignation',
  stage_changed: 'Changement d’étape',
  imported: 'Import',
  email_opened: 'Email ouvert',
  email_clicked: 'Email cliqué',
  email_replied: 'Réponse email',
  social_interaction: 'Interaction sociale',
};

export function activityLabel(type: string): string {
  return ACTIVITY_LABEL[type] ?? type;
}
